import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { Clock, Save } from 'lucide-react';
import { createAdditionalTaskSchema, type AdditionalTask, type CreateAdditionalTaskInput } from '@pms/shared';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { DatePicker } from '@/components/ui/date-picker';
import { TimePicker } from '@/components/ui/time-picker';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { additionalTasksService } from '@/services/additional-tasks.service';
import { usersService } from '@/services/users.service';
import { getErrorMessage } from '@/lib/errors';
import { fullName } from './taskConstants';

interface AdditionalWorkModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** When provided, the modal edits this record instead of creating a new one. */
  task?: AdditionalTask | null;
}

function durationLabel(start: string, end: string): string {
  const toMin = (t: string) => { const [h, m] = t.split(':'); return (Number(h) || 0) * 60 + (Number(m) || 0); };
  const mins = Math.max(0, toMin(end) - toMin(start));
  return `${Math.floor(mins / 60)}h ${mins % 60}m`;
}

export function AdditionalWorkModal({ open, onOpenChange, task }: AdditionalWorkModalProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-md overflow-y-auto">
        {/* Remount per open / per record so the form initialises cleanly from props. */}
        {open && <WorkForm key={task?.id ?? 'new'} task={task ?? null} onClose={() => onOpenChange(false)} />}
      </DialogContent>
    </Dialog>
  );
}

function WorkForm({ task, onClose }: { task: AdditionalTask | null; onClose: () => void }) {
  const qc = useQueryClient();
  const isEdit = !!task;
  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [workDate, setWorkDate] = useState(task?.work_date?.slice(0, 10) ?? '');
  const [startTime, setStartTime] = useState(task?.start_time?.slice(0, 5) ?? '');
  const [endTime, setEndTime] = useState(task?.end_time?.slice(0, 5) ?? '');
  const [assignedBy, setAssignedBy] = useState(task?.assigned_by ?? '');
  const [errors, setErrors] = useState<Record<string, string>>({});

  const { data: users } = useQuery({
    queryKey: ['tasks', 'assignee-picker'],
    queryFn: () => usersService.picker(),
  });

  const save = useMutation({
    mutationFn: async (payload: CreateAdditionalTaskInput) => {
      if (isEdit) await additionalTasksService.update(task!.id, payload);
      else await additionalTasksService.create(payload);
    },
    onSuccess: () => {
      toast.success(isEdit ? 'Additional work updated' : 'Additional work logged');
      qc.invalidateQueries({ queryKey: ['additional-tasks'] });
      onClose();
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  function submit() {
    const payload = {
      title, description: description || null, work_date: workDate,
      start_time: startTime, end_time: endTime, assigned_by: assignedBy || null,
    };
    const parsed = createAdditionalTaskSchema.safeParse(payload);
    if (!parsed.success) {
      const fe: Record<string, string> = {};
      for (const issue of parsed.error.issues) {
        const key = issue.path[0]?.toString() ?? 'form';
        if (!fe[key]) fe[key] = issue.message;
      }
      setErrors(fe);
      return;
    }
    setErrors({});
    save.mutate(parsed.data);
  }

  const duration = useMemo(
    () => (startTime && endTime ? durationLabel(startTime, endTime) : '0h 0m'),
    [startTime, endTime],
  );

  return (
    <>
      <DialogHeader>
        <DialogTitle>{isEdit ? 'Edit Additional Work' : 'Add Additional Work'}</DialogTitle>
      </DialogHeader>

      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Work Title <span className="text-rose-500">*</span></Label>
          <Input value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Enter work title" />
          {errors.title && <p className="text-xs text-rose-500">{errors.title}</p>}
        </div>
        <div className="space-y-1.5">
          <Label>Description</Label>
          <Textarea value={description} onChange={(e) => setDescription(e.target.value)} placeholder="Describe the work done" rows={3} />
        </div>
        <div className="space-y-1.5">
          <Label>Work Date <span className="text-rose-500">*</span></Label>
          <DatePicker value={workDate} onChange={setWorkDate} />
          {errors.work_date && <p className="text-xs text-rose-500">{errors.work_date}</p>}
        </div>
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-1.5">
            <Label>Start Time <span className="text-rose-500">*</span></Label>
            <TimePicker value={startTime} onChange={setStartTime} />
            {errors.start_time && <p className="text-xs text-rose-500">{errors.start_time}</p>}
          </div>
          <div className="space-y-1.5">
            <Label>End Time <span className="text-rose-500">*</span></Label>
            <TimePicker value={endTime} onChange={setEndTime} />
            {errors.end_time && <p className="text-xs text-rose-500">{errors.end_time}</p>}
          </div>
        </div>
        <div className="flex items-center gap-2 rounded-md bg-muted/50 px-3 py-2 text-sm">
          <Clock className="h-4 w-4 text-muted-foreground" />
          <span className="text-muted-foreground">Duration:</span>
          <span className="font-semibold text-primary">{duration}</span>
        </div>
        <div className="space-y-1.5">
          <Label>Assigned By</Label>
          <Select value={assignedBy} onValueChange={setAssignedBy}>
            <SelectTrigger><SelectValue placeholder="Select manager/admin who assigned" /></SelectTrigger>
            <SelectContent>
              {(users ?? []).map((u) => (
                <SelectItem key={u.id} value={u.id}>{fullName(u.first_name, u.last_name)}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="flex justify-end gap-2 border-t border-border/60 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="button" onClick={submit} disabled={save.isPending}>
          <Save className="h-4 w-4" /> {save.isPending ? 'Saving…' : isEdit ? 'Update' : 'Save'}
        </Button>
      </div>
    </>
  );
}
